import { platform } from "../platform/index.js";
// Coinbase Exchange public candles — free, keyless, real-time. Second
// crypto source behind Binance, so one exchange's outage never degrades
// crypto to delayed data. Product symbols (BTC-USD) ARE Coinbase product
// ids, so no mapping table is needed.

import { MarketDataError, type OHLCPoint } from "../market-data.js";
import { rangeToMs } from "./types.js";

/** Our interval vocabulary → Coinbase granularity seconds. Pure.
 * Coinbase supports exactly {60, 300, 900, 3600, 21600, 86400}. */
export function coinbaseGranularity(interval: string): number | null {
  const map: Record<string, number> = {
    "1m": 60,
    "5m": 300,
    "15m": 900,
    "60m": 3600,
    "1h": 3600,
    "1d": 86400,
  };
  return map[interval] ?? null;
}

export function coinbaseProduct(symbol: string): string | null {
  return /^[A-Z0-9]{2,10}-USD$/.test(symbol.trim().toUpperCase()) ? symbol.trim().toUpperCase() : null;
}

type CoinbaseCandle = [number, number, number, number, number, number]; // time, low, high, open, close, volume

export async function coinbaseCandles(symbol: string, range: string, interval: string): Promise<{ candles: OHLCPoint[]; currentPrice?: number }> {
  const product = coinbaseProduct(symbol);
  const granularity = coinbaseGranularity(interval);
  if (!product || !granularity) throw new MarketDataError(`Coinbase can't serve ${symbol} @ ${interval}`);

  const end = Date.now();
  // Coinbase caps 300 candles per request; clamp the window accordingly.
  const start = Math.max(end - rangeToMs(range), end - 300 * granularity * 1000);
  const url =
    `https://api.exchange.coinbase.com/products/${product}/candles` +
    `?granularity=${granularity}&start=${new Date(start).toISOString()}&end=${new Date(end).toISOString()}`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await platform.dataFetch(url, { signal: controller.signal });
    if (!response.ok) throw new MarketDataError(`Coinbase candles failed: ${response.status}`);
    const raw = (await response.json()) as CoinbaseCandle[];
    const candles: OHLCPoint[] = raw
      .map((c) => ({ date: new Date(c[0] * 1000), low: c[1], high: c[2], open: c[3], close: c[4], volume: c[5] }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    const last = candles[candles.length - 1];
    return { candles, currentPrice: last?.close };
  } finally {
    clearTimeout(timeout);
  }
}
